// @ts-strict-ignore
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Label, TextField } from '@usealloy/component-library';
import {
  RESERVED_KEYS,
  SERVICE_OPTION_TEXTFIELD_LABEL_MAP,
} from '../../../constants';
import {
  BASE_URL_OPTION_KEY,
  MODULES_OPTION_KEY,
} from 'flow/service/actionOptions';
import { ButtonWithMarginTop, ColumnBox, GrayText } from '../../styles';
import OptionBox from './OptionBox';
import type { ReadOnlyProp, ServiceOption } from '../../types';
import { createServiceOption } from '../../../serviceOptionHelpers';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { selectServiceActionOptionPublicIdInEditing } from 'flow/store/redux/selectors';
import { setServiceActionOptionPublicIdInEditing } from 'flow/store/redux/slice';

export interface AdditionalOptionsProps extends ReadOnlyProp {
  serviceOptions: ServiceOption[];
  onChange: (updatedData: ServiceOption[]) => void;
}

export default function AdditionalOptions({
  serviceOptions,
  onChange,
  readOnly,
}: AdditionalOptionsProps) {
  const dispatch = useAppDispatch();
  const actionOptionPublicId = useAppSelector(selectServiceActionOptionPublicIdInEditing);

  const [options, setOptions] = useState<ServiceOption[]>([]);
  const [additionalTextFieldOptions, setAdditionalTextFieldOptions] = useState<ServiceOption[]>([]);

  useEffect(() => {
    if (serviceOptions) {
      const advancedServiceOptions = [];
      const textFieldOptions = [];
      serviceOptions.forEach(option => {
        if (option.key === BASE_URL_OPTION_KEY
          || option.key === MODULES_OPTION_KEY
        ) {
          textFieldOptions.push(option);
        } else {
          advancedServiceOptions.push(option);
        }
      });
      setOptions(advancedServiceOptions);
      setAdditionalTextFieldOptions(textFieldOptions);
    }
  }, [serviceOptions]);

  const handleAddNewSection = useCallback(() => {
    const newSections = [...options];
    const newOption = createServiceOption();
    newSections.push(newOption);
    setOptions(newSections);
    dispatch(setServiceActionOptionPublicIdInEditing(newOption.publicId));
  }, [dispatch, options]);

  const handleOnEdit = useCallback(publicId => {
    dispatch(setServiceActionOptionPublicIdInEditing(publicId));
  }, [dispatch]);

  const handleOptionBoxOnChange = useCallback((
    publicId: string,
    newValuesAtIndex: ServiceOption,
  ) => {
    const updatedOptions = [...options];
    const indexToUpdate = updatedOptions.findIndex(option => option.publicId === publicId);
    if (newValuesAtIndex) {
      updatedOptions.splice(indexToUpdate, 1, newValuesAtIndex);
    } else {
      updatedOptions.splice(indexToUpdate, 1);
    }
    onChange([...additionalTextFieldOptions, ...updatedOptions]);
    dispatch(setServiceActionOptionPublicIdInEditing(null));
  }, [additionalTextFieldOptions, dispatch, onChange, options]);

  const AdditiveSections = useMemo(() => options.filter(
    option => !RESERVED_KEYS.includes(option.key),
  ).map((section: ServiceOption) => (
    <OptionBox
      key={section.publicId}
      input={section}
      isDisabled={readOnly || section.publicId !== actionOptionPublicId}
      onChange={handleOptionBoxOnChange}
      onEdit={handleOnEdit}
    />
  )), [handleOnEdit, handleOptionBoxOnChange, options, actionOptionPublicId, readOnly]);

  const handleTextFieldOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, i: number) => {
      const textFieldOptions = [...additionalTextFieldOptions];
      textFieldOptions[i].value = event.target.value;
      setAdditionalTextFieldOptions(textFieldOptions);
      onChange([...additionalTextFieldOptions, ...options]);
    }, [onChange, additionalTextFieldOptions, options]);

  const AdditionalTextFieldSections = useMemo(() => additionalTextFieldOptions.map((option, i) => (
    <TextField
      key={option.key}
      type="text"
      style={{ marginBottom: '0.5rem' }}
      label={SERVICE_OPTION_TEXTFIELD_LABEL_MAP.get(option.key)}
      value={option.value}
      disabled={readOnly}
      onChange={event => handleTextFieldOnChange(event, i)}
      // @ts-ignore
      autoComplete="stopItChrome"
    />
  )), [handleTextFieldOnChange, additionalTextFieldOptions, readOnly]);

  return (
    <ColumnBox>
      {AdditionalTextFieldSections.length > 0 && (
        <ColumnBox>
          {AdditionalTextFieldSections}
        </ColumnBox>
      )}
      <Label>Advanced Service Options</Label>
      {options.length ? (
        <ColumnBox data-dd-privacy="mask">
          {AdditiveSections}
        </ColumnBox>
      ) : (
        <GrayText>none added</GrayText>
      )}
      {!actionOptionPublicId && !readOnly && (
        <ButtonWithMarginTop onClick={handleAddNewSection}>
          Add Key/Value
        </ButtonWithMarginTop>
      )}
    </ColumnBox>
  );
}
